import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import { DealPermissionsEditor } from "@/src/components/permissions/DealPermissionsEditor";
import {
  DEFAULT_GUEST_PERMISSIONS,
  DEFAULT_INTERNAL_PERMISSIONS,
} from "@/src/components/permissions/dealPermissionConfig";
import { Button } from "@/src/components/tailwind/Button";
import { TextInput } from "@/src/components/tailwind/TextInput";
import { toasts } from "@/src/components/toasts/toasts";
import {
  DealRolePermissionsInput,
  DealRoleType,
  useCreateDealRoleMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

function defaultPermissions(roleType: DealRoleType): DealRolePermissionsInput {
  return roleType === DealRoleType.Guest
    ? { ...DEFAULT_GUEST_PERMISSIONS }
    : { ...DEFAULT_INTERNAL_PERMISSIONS };
}

export function CreateDealRoleModal(props: {
  open: boolean;
  onClose: () => void;
  roleType: DealRoleType;
  dealId?: string;
  firmId?: string;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const createRole = useCreateDealRoleMutation(client);

  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState<DealRolePermissionsInput>(
    () => defaultPermissions(props.roleType),
  );

  useEffect(() => {
    if (!props.open) {
      return;
    }
    setName("");
    setPermissions(defaultPermissions(props.roleType));
  }, [props.open, props.roleType]);

  function handleClose() {
    setName("");
    setPermissions(defaultPermissions(props.roleType));
    props.onClose();
  }

  async function handleCreate() {
    if (!name.trim() || createRole.isPending) {
      return;
    }

    try {
      await createRole.mutateAsync({
        input: {
          name: name.trim(),
          type: props.roleType,
          permissions,
          dealId: props.dealId,
          firmId: props.firmId,
        },
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["DealRoles"] }),
        queryClient.invalidateQueries({ queryKey: ["Firm"] }),
      ]);
      toasts.success("Role created");
      handleClose();
    } catch {
      toasts.error("Failed to create role");
    }
  }

  const title =
    props.roleType === DealRoleType.Guest
      ? "Create guest role"
      : "Create admin role";

  return (
    <AnimatedModal open={props.open} onClose={handleClose} size="lg">
      <div className="flex items-center justify-between mb-4">
        <p className="font-semibold text-foreground">{title}</p>
        <CloseIcon onClose={handleClose} />
      </div>

      <TextInput
        label="Name"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        placeholder="Role name"
        autoFocus
      />

      <div className="mt-4">
        <p className="text-sm font-semibold text-persian-990 mb-1">
          Permissions
        </p>
        <DealPermissionsEditor
          permissions={permissions}
          roleType={props.roleType}
          onChange={setPermissions}
          disabled={createRole.isPending}
        />
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button text="Cancel" variant="neutral" onClick={handleClose} />
        <Button
          text="Create"
          variant="positive"
          isLoading={createRole.isPending}
          isDisabled={!name.trim()}
          onClick={handleCreate}
        />
      </div>
    </AnimatedModal>
  );
}
